import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowUpRight, AlertTriangle, Check, TrendingUp } from "lucide-react";

const Overview = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { dashboardApi.overview().then(setData).catch(() => {}); }, []);

  if (!data) return <PageShell title="Overview"><div className="text-ink-muted">Loading…</div></PageShell>;

  return (
    <div className="p-8 md:p-12" data-testid="overview-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-3">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h1 className="h-display text-5xl md:text-6xl text-ink-body">
            Good to see you, <span className="italic text-artha-amber">{user?.name?.split(" ")[0]}</span>.
          </h1>
        </div>
        <Link to="/app/register" data-testid="overview-new-entity-button"
          className="hidden md:inline-flex items-center gap-2 bg-artha-amber text-artha-ink px-5 py-3 text-sm tracking-tight hover:bg-artha-cream transition-colors">
          Incorporate <ArrowUpRight size={14} />
        </Link>
      </div>

      {/* Top row */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-border-subtle border border-border-subtle">
        <HealthCard score={data.health_score} />
        <Metric label="Active entities" value={data.active_entities} sub={`${data.total_entities} total`} />
        <Metric label="Countries" value={data.countries_count} sub="jurisdictions" />
        <Metric label="Overdue" value={data.overdue_tasks} sub="compliance tasks" danger={data.overdue_tasks > 0} />
        <Metric label="Total spent" value={`$${Number(data.total_spent).toFixed(0)}`} sub="on Artha" />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
        <Panel title="Upcoming filings" cta={{ to: "/app/compliance", label: "See all" }} className="lg:col-span-7">
          {data.upcoming_tasks_list.length === 0 ? (
            <div className="py-8 text-center text-ink-muted text-sm">
              <Check size={28} className="mx-auto mb-3 text-artha-amber" />
              You're all caught up. Nothing due in the next 30 days.
            </div>
          ) : (
            <div className="divide-y divide-border-subtle">
              {data.upcoming_tasks_list.map((t) => (
                <div key={t.id} className="py-4 flex items-start justify-between gap-4" data-testid={`upcoming-task-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-ink-body">{t.title}</div>
                    <div className="text-xs text-ink-muted mt-1 font-mono-accent uppercase tracking-[0.18em]">
                      {t.category} · {t.priority}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-accent text-sm text-artha-amber">
                      {new Date(t.due_date).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-muted font-mono-accent">due</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Recent entities" cta={{ to: "/app/entities", label: "View all" }} className="lg:col-span-5">
          {data.recent_entities.length === 0 ? (
            <div className="py-8 text-center text-ink-muted text-sm">
              No entities yet. <Link to="/app/register" className="text-artha-amber">Start your first →</Link>
            </div>
          ) : (
            <div className="divide-y divide-border-subtle">
              {data.recent_entities.map((e) => (
                <Link to={`/app/entities/${e.id}`} key={e.id} className="py-4 flex items-center justify-between gap-4 hover:text-artha-amber transition-colors"
                  data-testid={`recent-entity-${e.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm">{e.company_name}</div>
                    <div className="text-xs text-ink-muted mt-1 font-mono-accent uppercase tracking-[0.18em]">
                      {e.country_name} · {e.entity_type_name}
                    </div>
                  </div>
                  <StatusPill status={e.status} />
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

const HealthCard = ({ score }) => {
  const color = score >= 80 ? "text-artha-amber" : score >= 50 ? "text-[#D98F1C]" : "text-[#C94F4F]";
  return (
    <div className="md:col-span-4 bg-surface-card p-8 flex flex-col justify-between" data-testid="health-score-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent">Compliance health</div>
        <TrendingUp size={16} className="text-artha-amber" />
      </div>
      <div className="mt-6">
        <div className={`h-display text-7xl ${color}`}>{score}</div>
        <div className="text-xs text-ink-muted font-mono-accent uppercase tracking-[0.2em] mt-2">out of 100</div>
      </div>
      <div className="mt-6 h-1 bg-border-subtle overflow-hidden">
        <div className="h-full bg-artha-amber" style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const Metric = ({ label, value, sub, danger }) => (
  <div className="md:col-span-2 bg-surface-card p-6 flex flex-col justify-between">
    <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent">{label}</div>
    <div className={`h-display text-4xl mt-4 ${danger ? "text-[#C94F4F]" : "text-ink-body"}`}>
      {value}
      {danger && <AlertTriangle size={14} className="inline ml-2 mb-2 text-[#C94F4F]" />}
    </div>
    <div className="text-xs text-ink-muted mt-2 font-mono-accent uppercase tracking-[0.18em]">{sub}</div>
  </div>
);

const Panel = ({ title, cta, children, className = "" }) => (
  <div className={`border border-border-subtle p-8 ${className}`}>
    <div className="flex items-end justify-between mb-6">
      <h2 className="h-display text-2xl">{title}</h2>
      {cta && <Link to={cta.to} className="text-xs text-artha-amber hover:text-artha-cream font-mono-accent uppercase tracking-[0.2em]">{cta.label}</Link>}
    </div>
    {children}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    draft: ["bg-border-subtle", "text-ink-muted"],
    payment_pending: ["bg-amber-900/40", "text-artha-amber"],
    in_progress: ["bg-blue-900/30", "text-blue-300"],
    active: ["bg-green-900/30", "text-green-300"],
  };
  const [bg, txt] = map[status] || map.draft;
  return <span className={`${bg} ${txt} px-2 py-1 text-[10px] font-mono-accent uppercase tracking-[0.2em]`}>{status.replace("_", " ")}</span>;
};

export const PageShell = ({ title, subtitle, children, action }) => (
  <div className="p-8 md:p-12">
    <div className="flex items-end justify-between mb-10">
      <div>
        {subtitle && <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-3">{subtitle}</div>}
        <h1 className="h-display text-5xl text-ink-body">{title}</h1>
      </div>
      {action}
    </div>
    {children}
  </div>
);

export { StatusPill };
export default Overview;
